<template>
  <div class="progress-monitor bg-surface/50 rounded-lg border border-line/20 p-6">
    <h3 class="text-xl font-semibold text-ink mb-4">Progress Monitor</h3>

    <!-- Execution Mode Badge -->
    <div class="mb-4">
      <span
        :class="[
          'px-3 py-1 text-xs font-semibold rounded-full',
          executionMode === 'web' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' :
          executionMode === 'local' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' :
          'bg-purple-500/20 text-purple-400 border border-purple-500/50'
        ]"
      >
        {{ executionModeName }}
      </span>
    </div>

    <!-- Live Progress (from new API) -->
    <div v-if="liveProgress && liveProgress.overallStatus === 'running'" class="mb-6 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 rounded-lg p-4">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <div class="text-emerald-400 animate-pulse text-xl">●</div>
          <div>
            <div class="text-lg font-semibold text-emerald-400">{{ getPhaseTitle(liveProgress.currentPhase) }}</div>
            <div class="text-xs text-muted">Started {{ formatRelativeTime(liveProgress.startTime) }}</div>
          </div>
        </div>
        <div v-if="currentPhaseData && currentPhaseData.eta" class="text-right">
          <div class="text-xs text-muted">ETA</div>
          <div class="text-sm font-medium text-emerald-400">{{ currentPhaseData.etaHuman }}</div>
        </div>
      </div>

      <!-- Progress Bar -->
      <div v-if="currentPhaseData && (currentPhaseData.seedsTotal || currentPhaseData.legosTotal)" class="mb-3">
        <div class="flex items-center justify-between text-xs mb-1">
          <!-- Legacy Phase 5 (now Phase 3): Show LEGOs and seeds -->
          <span v-if="liveProgress.currentPhase === 5 && currentPhaseData.legosTotal" class="text-ink">
            {{ currentPhaseData.legosCompleted || 0 }} / {{ currentPhaseData.legosTotal }} LEGOs
            <span class="text-faint ml-2">({{ currentPhaseData.seedsCompleted || 0 }} / {{ currentPhaseData.seedsTotal }} seeds)</span>
          </span>
          <!-- Other phases: Show agent breakdown or seeds -->
          <span v-else-if="currentPhaseData.agentCount" class="text-ink">
            {{ currentPhaseData.agentCount }} agents × {{ currentPhaseData.seedsPerAgent }} seeds/agent
          </span>
          <span v-else class="text-ink">{{ currentPhaseData.seedsCompleted || 0 }} / {{ currentPhaseData.seedsTotal }} seeds</span>

          <!-- Percentage: Use LEGOs for Phase 5, seeds for others -->
          <span class="text-emerald-400 font-medium">
            {{ liveProgress.currentPhase === 5 && currentPhaseData.legosTotal
              ? ((currentPhaseData.legosCompleted || 0) / currentPhaseData.legosTotal * 100).toFixed(1)
              : ((currentPhaseData.seedsCompleted || 0) / currentPhaseData.seedsTotal * 100).toFixed(1)
            }}%
          </span>
        </div>
        <div class="w-full bg-surface-2 rounded-full h-2.5">
          <div
            class="bg-emerald-500 h-2.5 rounded-full transition-all duration-500"
            :style="{ width: `${
              liveProgress.currentPhase === 5 && currentPhaseData.legosTotal
                ? ((currentPhaseData.legosCompleted || 0) / currentPhaseData.legosTotal * 100)
                : ((currentPhaseData.seedsCompleted || 0) / currentPhaseData.seedsTotal * 100)
            }%` }"
          ></div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div v-if="liveProgress.recentLogs && liveProgress.recentLogs.length > 0" class="bg-canvas/50 rounded p-2 max-h-32 overflow-y-auto">
        <div class="text-xs font-medium text-muted mb-1.5">Recent Activity</div>
        <div v-for="(log, i) in liveProgress.recentLogs.slice(0, 5)" :key="i" class="text-xs text-ink py-0.5">
          <span class="text-faint">{{ formatTime(log.time) }}</span>
          <span class="mx-1">•</span>
          <span>{{ log.message }}</span>
        </div>
      </div>
    </div>

    <!-- Simple status message when not running -->
    <div v-else class="text-center py-8 text-muted">
      <div class="text-sm">No active pipeline running</div>
      <div class="text-xs mt-2">Start a course generation to see live progress</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import { getApiUrl } from '@/services/api'

const props = defineProps({
  courseCode: {
    type: String,
    required: true
  },
  pollInterval: {
    type: Number,
    default: 5000
  },
  seedCount: {
    type: Number,
    default: null
  }
})

const liveProgress = ref(null)
const executionMode = ref('web')

const executionModeName = computed(() => {
  if (executionMode.value === 'web') return '● Web Mode'
  if (executionMode.value === 'local') return '● Local Mode'
  return '● Hybrid Mode'
})

const currentPhaseData = computed(() => {
  if (!liveProgress.value || !liveProgress.value.currentPhase) return null
  return liveProgress.value.phases[liveProgress.value.currentPhase]
})

function getPhaseTitle(phaseNum) {
  // APML v9.0 phase naming
  const titles = {
    1: 'Phase 1: Translation + LEGO Extraction',
    3: 'Phase 3: Basket Generation',
    'manifest': 'Manifest Compilation',
    'audio': 'Audio Generation',
    // Legacy mappings for backward compatibility
    5: 'Phase 3: Basket Generation',  // Legacy Phase 5 → New Phase 3
    7: 'Manifest Compilation',        // Legacy Phase 7 → Manifest
    8: 'Audio Generation'             // Legacy Phase 8 → Audio
  }
  return titles[phaseNum] || `Phase ${phaseNum}`
}

function formatTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', { hour12: false })
}

function formatRelativeTime(timestamp) {
  if (!timestamp) return ''
  const now = Date.now()
  const then = new Date(timestamp).getTime()
  const diff = Math.floor((now - then) / 1000) // seconds

  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  return `${Math.floor(diff / 3600)}h ago`
}

async function fetchProgress() {
  try {
    const apiBaseUrl = getApiUrl()
    const response = await axios.get(`${apiBaseUrl}/api/courses/${props.courseCode}/progress`, {
      headers: {}
    })
    liveProgress.value = response.data
  } catch (error) {
    // Silently fail - progress endpoint might not exist yet
    console.log('[ProgressMonitor] Progress not available:', error.message)
  }
}

let interval = null

onMounted(() => {
  fetchProgress()
  interval = setInterval(fetchProgress, props.pollInterval)
})

onUnmounted(() => {
  if (interval) clearInterval(interval)
})
</script>

<style scoped>
/* Light-mode only: dark mode is untouched (no rules outside this selector). */

/* Card separation: faint border/half-opacity surface vanish on slate canvas.
   Give the card a solid white surface, a visible --line border and a subtle shadow. */
:root[data-theme="light"] .progress-monitor {
  background-color: var(--surface);
  border-color: var(--line);
  box-shadow: 0 1px 3px rgb(15 23 42 / 0.06), 0 1px 2px rgb(15 23 42 / 0.04);
}

/* Emerald accents (phase title / ETA / percentage / live dot):
   emerald-400 #34d399 on white ≈ 1.6:1. Use emerald-700 #047857 ≈ 4.95:1. */
:root[data-theme="light"] .progress-monitor :deep(.text-emerald-400) {
  color: #047857;
}
/* Progress fill + live gradient panel: keep emerald hue but ensure the fill
   and the tinted live panel read against white. */
:root[data-theme="light"] .progress-monitor :deep(.bg-emerald-500) {
  background-color: #059669;
}
:root[data-theme="light"] .progress-monitor :deep(.border-emerald-500\/30),
:root[data-theme="light"] .progress-monitor :deep(.border-emerald-500\/50) {
  border-color: #059669;
}

/* Execution-mode pill (web=emerald, local=blue, hybrid=purple):
   *-400 text on a *-500/20 fill is washed out on white. Darken text to AA
   and give the fill a touch more saturation while keeping the hue family. */
:root[data-theme="light"] .progress-monitor :deep(.text-blue-400) {
  color: #1d4ed8; /* blue-700 ≈ 5.2:1 on white */
}
:root[data-theme="light"] .progress-monitor :deep(.text-purple-400) {
  color: #7e22ce; /* purple-700 ≈ 5.0:1 on white */
}
:root[data-theme="light"] .progress-monitor :deep(.bg-emerald-500\/20) {
  background-color: rgb(16 185 129 / 0.14);
}
:root[data-theme="light"] .progress-monitor :deep(.bg-blue-500\/20) {
  background-color: rgb(59 130 246 / 0.14);
}
:root[data-theme="light"] .progress-monitor :deep(.bg-purple-500\/20) {
  background-color: rgb(168 85 247 / 0.14);
}
:root[data-theme="light"] .progress-monitor :deep(.border-blue-500\/50) {
  border-color: #2563eb;
}
:root[data-theme="light"] .progress-monitor :deep(.border-purple-500\/50) {
  border-color: #9333ea;
}
</style>
